import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RotateCcw } from 'lucide-react'
import { useUIStore } from '@store/uiStore'
import { DEFAULT_CREDS } from '@features/dashboards/testing-ground/data'
import type { Creds, LogEntry } from '@features/dashboards/testing-ground/types'
import { STAGES } from './data'
import { StageRail } from './components/StageRail'
import { StageCompleteNav } from './components/StageCompleteNav'
import { AccountSetupStage } from './components/AccountSetupStage'
import { IdvBiometricsStage } from './components/IdvBiometricsStage'
import { KycCascadeStage } from './components/KycCascadeStage'
import { KybStage } from './components/KybStage'
import { AmlStage } from './components/AmlStage'
import { FraudStage } from './components/FraudStage'
import { TransactionMonitoringStage } from './components/TransactionMonitoringStage'
import { WorkflowBuilderStage } from './components/WorkflowBuilderStage'
import { isProductEnabled, fetchExistingCustomerSelection } from './components/ExistingCustomerPanel'
import type { ExistingCustomerSelection } from './components/ExistingCustomerPanel'
import type { InteractionDiagramStage } from './interactionDiagrams'
import type { CustomerMode } from './components/NewExistingToggle'
import { extractAmlStep, extractFraudChecks, outcomeLabel } from './utils'
import { useOpsCenterStore } from '@features/dashboards/components/OpsCenterMockup/store'
import { deriveConfigFromWorkflow } from './workflowDerivedConfig'
import type { ExperienceMode } from './components/ExperienceModeSwitcher'
import { platformJourneysApi, dealsApi } from '@api/client'
import type { PlatformJourneyRecord } from '@api/client'
import type { AnyObj, EnvAccounts, SimAlert, StageId, StageStatus } from './types'
import type { AiConfigureSuggestions, AiStageKey } from './aiConfigure'
import { ALL_AI_STAGE_KEYS, useAiStageSuggestion } from './aiConfigure'

// Stages that aren't gated by an existing customer's real Scope enablement — they're
// either the setup/tooling surface itself (Account Setup) or a technical builder tool
// independent of any one customer's provisioned products (Workflow Builder).
const UNGATED_STAGE_IDS: StageId[] = ['account-setup', 'workflow-builder']

// A rep-entered API key never gets stored against the deal — only cached in this
// browser so it survives a refresh but never leaves the client except straight to
// FrankieOne's API via the existing proxy.
const MANUAL_CREDS_KEY = 'pjt-live-credentials'
interface ManualCreds { apiKey: string; customerId: string; childCustomerId: string; apiBase: string }
function loadManualCreds(defaults: ManualCreds): ManualCreds {
  try {
    const stored = localStorage.getItem(MANUAL_CREDS_KEY)
    if (stored) return { ...defaults, ...JSON.parse(stored) as Partial<ManualCreds> }
  } catch { /* ignore */ }
  return defaults
}

export function PlatformJourneyTool() {
  const [, setLog] = useState<LogEntry[]>([])
  const logIdRef = useRef(0)

  const [activeStage, setActiveStage] = useState<StageId>('account-setup')
  // Shared across every stage — picking Guided on Account Setup keeps
  // every later stage the rep navigates to in that same mode, rather than each stage
  // independently defaulting back to Classic.
  const [experienceMode, setExperienceMode] = useState<ExperienceMode>('classic')
  // The Workflow Builder stage uses the full-width editor; other stages stay constrained.
  const setContentFullWidth = useUIStore((s) => s.setContentFullWidth)
  useEffect(() => {
    setContentFullWidth(activeStage === 'workflow-builder')
    return () => setContentFullWidth(false)
  }, [activeStage, setContentFullWidth])
  const [customerMode, setCustomerMode] = useState<CustomerMode>('new')
  const [existingCustomer, setExistingCustomer] = useState<ExistingCustomerSelection | null>(null)
  const [customerName, setCustomerName] = useState<string | null>(null)
  // The saved "Platform Journey" record for a brand-new customer walkthrough —
  // created once Account Setup provisions accounts, then updated as each stage
  // saves its own configuration. Lets a rep reload and validate what was
  // configured later instead of losing it on refresh. Existing-customer mode
  // never touches this — it reads DealHub's real Scope data instead.
  const [journeyId, setJourneyId] = useState<number | null>(null)
  const [journeyRecord, setJourneyRecord] = useState<PlatformJourneyRecord | null>(null)

  const refreshJourney = useCallback(async (id: number) => {
    const res = await platformJourneysApi.get(id)
    if (res.success && res.data) setJourneyRecord(res.data)
  }, [])

  const handleJourneyLoaded = useCallback((record: PlatformJourneyRecord) => {
    setJourneyId(record.id)
    setJourneyRecord(record)
  }, [])
  const [envAccounts, setEnvAccounts] = useState<EnvAccounts[]>([])
  const [accountSetupRunning, setAccountSetupRunning] = useState(false)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [workflowResult, setWorkflowResult] = useState<AnyObj | null>(null)
  const [idvRunning, setIdvRunning] = useState(false)
  const [kycRunning, setKycRunning] = useState(false)
  const [tmRunning, setTmRunning] = useState(false)
  const [tmAlerts, setTmAlerts] = useState<SimAlert[] | null>(null)
  const [kybRunning, setKybRunning] = useState(false)
  const [kybResult, setKybResult] = useState<AnyObj | null>(null)

  // AI Configure from Scope — an existing customer's Scope record, translated
  // into pre-built suggestions for every in-scope stage. Each stage gets its
  // OWN token (rather than one shared counter) — a fresh full batch bumps
  // every stage's token together (so they all re-open for review, same as
  // before), but a chat-driven revision to a single stage (handleAiSuggestionPatch)
  // only bumps that one stage's token, re-opening it without resetting the
  // applied/dismissed state of the other 8 — a shared counter would reset all
  // nine every time, silently un-applying stages the rep already accepted.
  const [aiSuggestions, setAiSuggestions] = useState<AiConfigureSuggestions | null>(null)
  const nextAiTokenRef = useRef(0)
  const [aiStageTokens, setAiStageTokens] = useState<Record<AiStageKey, number>>(
    () => Object.fromEntries(ALL_AI_STAGE_KEYS.map((k) => [k, 0])) as Record<AiStageKey, number>,
  )
  const handleAiSuggestions = useCallback((suggestions: AiConfigureSuggestions) => {
    setAiSuggestions(suggestions)
    nextAiTokenRef.current += 1
    const tok = nextAiTokenRef.current
    setAiStageTokens(Object.fromEntries(ALL_AI_STAGE_KEYS.map((k) => [k, tok])) as Record<AiStageKey, number>)
  }, [])
  // A chat reply asking to change a stage's suggested config (rather than just
  // include/exclude it) reports back here — `suggestion` is always the COMPLETE
  // replacement object for that stage (see aiConfigure.ts's AiChatUpdateSuggestionAction),
  // so this is a full overwrite, not a merge.
  const handleAiSuggestionPatch = useCallback((stage: AiStageKey, suggestion: unknown) => {
    setAiSuggestions((prev) => {
      if (!prev) return prev
      // AiConfigureSuggestions has no index signature (each key's value type
      // differs), so a dynamic single-key update needs an escape hatch here —
      // every other read of aiSuggestions elsewhere stays fully typed.
      return { ...(prev as unknown as Record<string, unknown>), [stage]: suggestion } as unknown as AiConfigureSuggestions
    })
    nextAiTokenRef.current += 1
    const tok = nextAiTokenRef.current
    setAiStageTokens((prev) => ({ ...prev, [stage]: tok }))
  }, [])
  // Lifted (rather than computed inside each stage component alone) so
  // Account Setup can also show its own checklist of what got suggested —
  // check to keep, uncheck to exclude a stage the rep knows isn't actually
  // needed — right where the suggestions were generated, in sync with each
  // stage's own banner instead of drifting out of sync with a separate copy.
  const idvAi = useAiStageSuggestion(
    aiSuggestions?.idv ? { defaultProvider: aiSuggestions.idv.defaultProvider, autoRunKyc: aiSuggestions.idv.autoRunKyc } : undefined,
    aiStageTokens.idv,
  )
  const cascadeAi = useAiStageSuggestion(aiSuggestions?.cascade?.checks, aiStageTokens.cascade)
  const duplicatesAi = useAiStageSuggestion(aiSuggestions?.duplicates?.customRules, aiStageTokens.duplicates)
  const riskAi = useAiStageSuggestion(aiSuggestions?.risk?.riskFactors, aiStageTokens.risk)
  const kybAi = useAiStageSuggestion(
    aiSuggestions?.kyb ? { auChecks: aiSuggestions.kyb.auChecks, ownership: aiSuggestions.kyb.ownership } : undefined,
    aiStageTokens.kyb,
  )
  const amlAi = useAiStageSuggestion(aiSuggestions?.aml?.config, aiStageTokens.aml)
  const fraudAi = useAiStageSuggestion(aiSuggestions?.fraud?.signals, aiStageTokens.fraud)
  const tmAi = useAiStageSuggestion(aiSuggestions?.tm?.config, aiStageTokens.tm)
  const workflowAi = useAiStageSuggestion(aiSuggestions?.workflow, aiStageTokens.workflow)

  // The Workflow Builder slate's selection persists in its own store even after
  // navigating away (the stage stays mounted) — reading it here lets later slates
  // (KYC + Cascade, IDV & Biometrics) confirm what it already implies instead of
  // asking the rep to configure the same thing twice. Only meaningful for a brand
  // new customer walkthrough; existing-customer mode reads DealHub's own Scope
  // data instead and never touches Workflow Builder.
  const opsCenterWorkflows = useOpsCenterStore((s) => s.workflows)
  const opsCenterSelectedWorkflowId = useOpsCenterStore((s) => s.selectedWorkflowId)
  const builderConfig = useMemo(() => {
    if (customerMode === 'existing') return null
    const wf = opsCenterWorkflows.find((w) => w.id === opsCenterSelectedWorkflowId)
    return wf ? deriveConfigFromWorkflow(wf) : null
  }, [customerMode, opsCenterWorkflows, opsCenterSelectedWorkflowId])

  const platformVersion: 'v1' | 'v2' = customerMode === 'existing'
    ? (existingCustomer?.scopeData?.generalInfo.platformVersion === 'v1' ? 'v1' : 'v2')
    : (envAccounts[0]?.parent.version ?? 'v2')
  const effectiveCustomerName = customerMode === 'existing' ? (existingCustomer?.deal.customer_name ?? null) : customerName

  // For an existing customer, the customerId/childCustomerId are already known from
  // their own recorded Scope data. For a brand-new walkthrough there's no such record,
  // so the rep types those in via LiveCredentialsPanel instead. Either way, the API key
  // itself is never derivable from anything DealHub stores — it's always rep-entered.
  const liveAccount = useMemo(() => {
    const gi = existingCustomer?.scopeData?.generalInfo
    if (customerMode !== 'existing' || !gi) return null
    const uatId = gi.parentUatId.trim()
    if (uatId) return { env: 'UAT' as const, customerId: uatId, childCustomerId: gi.childUatId.trim(), apiBase: 'https://api.uat.frankie.one/v2' }
    const prodId = gi.parentProdId.trim()
    if (prodId) return { env: 'PROD' as const, customerId: prodId, childCustomerId: gi.childProdId.trim(), apiBase: 'https://api.frankie.one/v2' }
    return null
  }, [customerMode, existingCustomer])

  const [manualCreds, setManualCreds] = useState<ManualCreds>(() => loadManualCreds({
    apiKey: '', customerId: '', childCustomerId: '', apiBase: DEFAULT_CREDS.apiBase,
  }))
  function handleCredsChange(next: Creds) {
    const updated: ManualCreds = {
      apiKey: next.apiKey,
      customerId: liveAccount ? manualCreds.customerId : next.customerId,
      childCustomerId: liveAccount ? manualCreds.childCustomerId : next.childCustomerId,
      apiBase: liveAccount ? manualCreds.apiBase : next.apiBase,
    }
    setManualCreds(updated)
    try { localStorage.setItem(MANUAL_CREDS_KEY, JSON.stringify(updated)) } catch { /* ignore */ }
  }

  const creds: Creds = useMemo(() => (
    liveAccount
      ? { ...DEFAULT_CREDS, customerId: liveAccount.customerId, childCustomerId: liveAccount.childCustomerId, apiBase: liveAccount.apiBase, apiKey: manualCreds.apiKey }
      : { ...DEFAULT_CREDS, customerId: manualCreds.customerId, childCustomerId: manualCreds.childCustomerId, apiBase: manualCreds.apiBase, apiKey: manualCreds.apiKey }
  ), [liveAccount, manualCreds])
  const hasCreds = !!(creds.customerId && creds.apiKey)

  const addLog = useCallback((entry: Omit<LogEntry, 'id' | 'ts'>) => {
    logIdRef.current += 1
    const id = logIdRef.current
    const ts = new Date().toLocaleTimeString()
    setLog(prev => [...prev, { ...entry, id, ts }])
  }, [])

  function clearProgressState() {
    setCustomerName(null)
    setEnvAccounts([])
    setEntityId(null)
    setWorkflowResult(null)
    setTmAlerts(null)
    setKybResult(null)
    setJourneyId(null)
    setJourneyRecord(null)
    setAiSuggestions(null)
  }

  function resetJourney() {
    setCustomerMode('new')
    setExistingCustomer(null)
    clearProgressState()
    setActiveStage('account-setup')
  }

  // Switching customer context (New/Existing toggle, or picking a different existing
  // customer) must clear the live run state — otherwise e.g. IDV stays permanently
  // ticked "pass" from a previous customer's run even though the newly-selected
  // customer never went through IDV in this session.
  function handleCustomerModeChange(mode: CustomerMode) {
    setCustomerMode(mode)
    clearProgressState()
  }

  function handleExistingCustomerChange(selection: ExistingCustomerSelection | null) {
    const changed = existingCustomer?.deal.id !== selection?.deal.id
    setExistingCustomer(selection)
    if (changed) clearProgressState()
  }

  // AI Configure (and the rest of the Existing Customer surface) previously
  // only ever saw a deal a rep manually searched for — a New Customer
  // walkthrough had no way to use the Scope record for the actual deal it's
  // for, short of re-searching for it as a "Test" convenience. A `?dealId=`
  // in the URL (e.g. a future "Open Platform Journey" link from a deal's own
  // page) auto-resolves that deal's own Scope record into `existingCustomer`
  // on mount — in EITHER customerMode — without requiring a re-search.
  // Deliberately runs once at mount only, not on every URL change, so it
  // never fights a rep who's already picked a different customer by hand.
  const [searchParams] = useSearchParams()
  const [dealContextResolved, setDealContextResolved] = useState(false)
  const [dealContextError, setDealContextError] = useState<string | null>(null)
  useEffect(() => {
    const dealIdParam = searchParams.get('dealId')
    const dealId = dealIdParam ? Number(dealIdParam) : NaN
    if (!Number.isFinite(dealId)) return
    let cancelled = false
    void (async () => {
      const dealRes = await dealsApi.get(dealId)
      if (cancelled) return
      if (!dealRes.success || !dealRes.data) {
        setDealContextError('Could not load the deal referenced in the URL.')
        return
      }
      const { selection, error } = await fetchExistingCustomerSelection(dealRes.data)
      if (cancelled) return
      handleExistingCustomerChange(selection)
      setDealContextResolved(true)
      if (error) setDealContextError(error)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Derive each stage's status for the rail ──────────────────────────────
  const kycOutcome = workflowResult ? outcomeLabel(workflowResult) : ''
  const amlStep = extractAmlStep(workflowResult)
  const fraudChecks = extractFraudChecks(workflowResult)

  function toStatus(outcome: string): StageStatus {
    if (outcome === 'PASS') return 'pass'
    if (outcome === 'FAIL') return 'fail'
    if (outcome === 'REFER') return 'refer'
    // Reachable only where the caller already confirmed a result exists (see call
    // sites below) — an unrecognised outcome (e.g. a raw "COMPLETED" execution
    // state with no risk level to categorise) still means the stage finished, so
    // this must NOT fall back to 'pending' or the stage would look stuck forever
    // (no rail checkmark, no "Continue to next stage" banner) despite having run.
    return 'skipped'
  }

  const statuses: Record<StageId, StageStatus> = {
    'account-setup': accountSetupRunning ? 'running' : envAccounts.length > 0 ? 'pass' : 'pending',
    idv: idvRunning ? 'running' : entityId ? 'pass' : 'pending',
    kyc: kycRunning ? 'running' : workflowResult ? toStatus(kycOutcome) : 'pending',
    aml: !workflowResult ? 'pending' : !amlStep ? 'skipped' : toStatus(
      amlStep.result === 'CLEAR' || amlStep.result === 'PASS' ? 'PASS' : amlStep.result === 'FAIL' ? 'FAIL' : 'REFER',
    ),
    fraud: !workflowResult ? 'pending' : fraudChecks.length === 0 ? 'skipped' : (
      fraudChecks.some(c => c.result === 'FAIL') ? 'fail' : 'pass'
    ),
    tm: tmRunning ? 'running' : tmAlerts === null ? 'pending' : tmAlerts.length === 0 ? 'pass' : 'refer',
    kyb: kybRunning ? 'running' : kybResult ? toStatus(outcomeLabel(kybResult)) : 'pending',
    'workflow-builder': 'pending',
  }

  // Once an existing customer is selected, only show the products actually
  // provisioned for them — everything else disappears from the rail rather than
  // just showing a "not configured" message on click.
  const visibleStageIds = useMemo(() => {
    if (customerMode !== 'existing' || !existingCustomer) return STAGES.map((s) => s.id)
    return STAGES
      .filter((s) => UNGATED_STAGE_IDS.includes(s.id) || isProductEnabled(s.id as InteractionDiagramStage, existingCustomer.scopeData))
      .map((s) => s.id)
  }, [customerMode, existingCustomer])

  useEffect(() => {
    if (!visibleStageIds.includes(activeStage)) setActiveStage('account-setup')
  }, [visibleStageIds, activeStage])

  function nextVisibleStageId(stageId: StageId): StageId | null {
    const idx = visibleStageIds.indexOf(stageId)
    return idx === -1 || idx === visibleStageIds.length - 1 ? null : visibleStageIds[idx + 1]
  }

  // Once a stage's status settles (pass/fail/refer/skipped — anything but pending/running),
  // offer a one-click way to move on to the next visible stage, regardless of which
  // experience mode (Classic/Guided) produced that result.
  function renderStageAdvance(stageId: StageId) {
    const status = statuses[stageId]
    if (status === 'pending' || status === 'running') return null
    const nextId = nextVisibleStageId(stageId)
    if (!nextId) return null
    const nextLabel = STAGES.find((s) => s.id === nextId)?.label ?? nextId
    return <StageCompleteNav nextLabel={nextLabel} onNext={() => setActiveStage(nextId)} />
  }

  // Workflow Builder has no pass/fail run of its own — "finished" just means the
  // rep has a workflow (or recipe) open and configured, which WorkflowBuilderStage
  // itself is in the best position to know. It decides when to show this.
  const workflowBuilderNextId = nextVisibleStageId('workflow-builder')
  const workflowBuilderNextLabel = workflowBuilderNextId
    ? (STAGES.find((s) => s.id === workflowBuilderNextId)?.label ?? workflowBuilderNextId)
    : null

  return (
    <div style={{ background: '#dfe2e7', minHeight: '100%' }}>
      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(7px) } to { opacity: 1; transform: none } }`}</style>

      <div className="px-5 pb-8 pt-4 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-[#1e293b]">Platform Journey Prototype</h2>
            <p className="text-[11px] text-[#475569] mt-0.5">
              Set up the customer shell, then run one test identity live through IDV, KYC + cascade, AML and fraud — with a simulated Transaction Monitoring stage on top.
            </p>
            {dealContextError && (
              <p className="text-[10px] text-amber-700 mt-1">{dealContextError}</p>
            )}
            {liveAccount && (
              <div className="flex items-center gap-2 mt-2">
                <span
                  className="px-2 py-0.5 rounded-[6px] text-[10px] font-bold uppercase tracking-wider"
                  style={liveAccount.env === 'UAT'
                    ? { background: 'rgba(251,191,36,.1)', border: '1px solid rgba(251,191,36,.3)', color: '#b45309' }
                    : { background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', color: '#b91c1c' }
                  }
                >
                  {liveAccount.env}
                </span>
                <span
                  className="px-2.5 py-0.5 rounded-[6px] text-[11px] font-mono text-[#64748b]"
                  style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
                >
                  {liveAccount.customerId}
                </span>
              </div>
            )}
          </div>
          {(envAccounts.length > 0 || entityId || workflowResult || tmAlerts !== null || kybResult || existingCustomer) && (
            <button
              onClick={resetJourney}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-medium shrink-0 transition-colors"
              style={{ background: '#f1f5f9', border: '1px solid #283a57', color: '#4f46e5' }}
            >
              <RotateCcw size={11} /> Reset Journey
            </button>
          )}
        </div>

        <StageRail statuses={statuses} activeStage={activeStage} onSelect={setActiveStage} visibleStageIds={visibleStageIds} />

        {/* Every stage stays mounted the whole session — only the active one is shown
            (via CSS, not conditional rendering) — so switching stages never wipes out
            in-progress form state on the ones you navigate away from. */}
        <div className="pt-1">
          <div style={{ display: activeStage === 'account-setup' ? 'block' : 'none' }}>
            <AccountSetupStage
              customerMode={customerMode}
              onCustomerModeChange={handleCustomerModeChange}
              existingCustomer={existingCustomer}
              onExistingCustomerChange={handleExistingCustomerChange}
              hasDealContext={dealContextResolved}
              onRunningChange={setAccountSetupRunning}
              onComplete={(accounts, name) => { setEnvAccounts(accounts); setCustomerName(name) }}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneyLoaded={handleJourneyLoaded}
              onAiSuggestions={handleAiSuggestions}
              onAiSuggestionPatch={handleAiSuggestionPatch}
              aiSuggestions={aiSuggestions}
              idvAi={idvAi}
              cascadeAi={cascadeAi}
              duplicatesAi={duplicatesAi}
              riskAi={riskAi}
              kybAi={kybAi}
              amlAi={amlAi}
              fraudAi={fraudAi}
              tmAi={tmAi}
              workflowAi={workflowAi}
            />
            {renderStageAdvance('account-setup')}
          </div>
          <div style={{ display: activeStage === 'idv' ? 'block' : 'none' }}>
            <IdvBiometricsStage
              creds={creds}
              hasCreds={hasCreds}
              onCredsChange={handleCredsChange}
              credsLocked={!!liveAccount}
              addLog={addLog}
              entityId={entityId}
              onEntityCreated={setEntityId}
              onRunningChange={setIdvRunning}
              customerMode={customerMode}
              existingCustomer={existingCustomer}
              customerName={effectiveCustomerName}
              platformVersion={platformVersion}
              builderConfig={builderConfig}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneySaved={() => journeyId && void refreshJourney(journeyId)}
              aiSuggestions={aiSuggestions}
              idvAi={idvAi}
            />
            {renderStageAdvance('idv')}
          </div>
          <div style={{ display: activeStage === 'kyc' ? 'block' : 'none' }}>
            <KycCascadeStage
              creds={creds}
              hasCreds={hasCreds}
              addLog={addLog}
              entityId={entityId}
              onEntityCreated={setEntityId}
              workflowResult={workflowResult}
              onResult={(wr) => setWorkflowResult(wr)}
              onRunningChange={setKycRunning}
              customerMode={customerMode}
              existingCustomer={existingCustomer}
              customerName={effectiveCustomerName}
              platformVersion={platformVersion}
              builderConfig={builderConfig}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneySaved={() => journeyId && void refreshJourney(journeyId)}
              aiSuggestions={aiSuggestions}
              cascadeAi={cascadeAi}
              duplicatesAi={duplicatesAi}
              riskAi={riskAi}
            />
            {renderStageAdvance('kyc')}
          </div>
          <div style={{ display: activeStage === 'kyb' ? 'block' : 'none' }}>
            <KybStage
              creds={creds}
              hasCreds={hasCreds}
              addLog={addLog}
              onRunningChange={setKybRunning}
              onComplete={setKybResult}
              customerMode={customerMode}
              existingCustomer={existingCustomer}
              customerName={effectiveCustomerName}
              platformVersion={platformVersion}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneySaved={() => journeyId && void refreshJourney(journeyId)}
              aiSuggestions={aiSuggestions}
              kybAi={kybAi}
            />
            {renderStageAdvance('kyb')}
          </div>
          <div style={{ display: activeStage === 'aml' ? 'block' : 'none' }}>
            <AmlStage
              creds={creds}
              hasCreds={hasCreds}
              addLog={addLog}
              entityId={entityId}
              workflowResult={workflowResult}
              customerMode={customerMode}
              existingCustomer={existingCustomer}
              customerName={effectiveCustomerName}
              platformVersion={platformVersion}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneySaved={() => journeyId && void refreshJourney(journeyId)}
              aiSuggestions={aiSuggestions}
              amlAi={amlAi}
            />
            {renderStageAdvance('aml')}
          </div>
          <div style={{ display: activeStage === 'fraud' ? 'block' : 'none' }}>
            <FraudStage
              workflowResult={workflowResult}
              customerMode={customerMode}
              existingCustomer={existingCustomer}
              customerName={effectiveCustomerName}
              platformVersion={platformVersion}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneySaved={() => journeyId && void refreshJourney(journeyId)}
              aiSuggestions={aiSuggestions}
              fraudAi={fraudAi}
            />
            {renderStageAdvance('fraud')}
          </div>
          <div style={{ display: activeStage === 'tm' ? 'block' : 'none' }}>
            <TransactionMonitoringStage
              onRunningChange={setTmRunning}
              onComplete={setTmAlerts}
              customerMode={customerMode}
              existingCustomer={existingCustomer}
              customerName={effectiveCustomerName}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneySaved={() => journeyId && void refreshJourney(journeyId)}
              aiSuggestions={aiSuggestions}
              tmAi={tmAi}
            />
            {renderStageAdvance('tm')}
          </div>
          <div style={{ display: activeStage === 'workflow-builder' ? 'block' : 'none' }}>
            <WorkflowBuilderStage
              nextStageLabel={workflowBuilderNextLabel}
              onAdvance={workflowBuilderNextId ? () => setActiveStage(workflowBuilderNextId) : undefined}
              experienceMode={experienceMode}
              onExperienceModeChange={setExperienceMode}
              customerMode={customerMode}
              existingCustomer={existingCustomer}
              journeyId={journeyId}
              journeyRecord={journeyRecord}
              onJourneySaved={() => journeyId && void refreshJourney(journeyId)}
              aiSuggestions={aiSuggestions}
              workflowAi={workflowAi}
              idvAi={idvAi}
              cascadeAi={cascadeAi}
              duplicatesAi={duplicatesAi}
              kybAi={kybAi}
              amlAi={amlAi}
              fraudAi={fraudAi}
              tmAi={tmAi}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
